import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useT } from "../i18n/LanguageContext.jsx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RISK_COLORS = {
  "Very Low": "#10B981",
  Low: "#84CC16",
  Intermediate: "#FBBF24",
  "Intermediate-High": "#F97316",
  High: "#EF4444",
  "Very High": "#7F1D1D",
};

const WINDOWS = [7, 30, 90, "all"];

function pct(v) {
  if (v == null) return "—";
  return `${(v * 100).toFixed(1)}%`;
}

function HeroAccuracy({ data, t }) {
  const a = data?.overall?.accuracy;
  const validated = data?.overall?.validated ?? 0;

  if (validated === 0) {
    return (
      <div className="border border-slate-200 p-8 clinical-grid">
        <div className="data-label mb-3">{t("acc.headline.label")}</div>
        <div className="text-sm text-slate-500 max-w-md">{t("acc.headline.empty")}</div>
      </div>
    );
  }

  // Big ring with accuracy %, drawn in SVG (avoids extra libs).
  const r = 88;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - (a ?? 0));
  const color =
    a >= 0.85 ? "#10B981" : a >= 0.7 ? "#FBBF24" : a >= 0.5 ? "#F97316" : "#EF4444";

  return (
    <div className="border border-slate-200 p-8 flex items-center gap-10" data-testid="acc-headline">
      <svg width="220" height="220" viewBox="0 0 220 220">
        <circle cx="110" cy="110" r={r} stroke="#E2E8F0" strokeWidth="14" fill="none" />
        <circle
          cx="110"
          cy="110"
          r={r}
          stroke={color}
          strokeWidth="14"
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 110 110)"
          style={{ transition: "stroke-dashoffset 400ms ease" }}
        />
        <text
          x="110"
          y="118"
          textAnchor="middle"
          fontSize="40"
          fontFamily="IBM Plex Mono, monospace"
          fontWeight="600"
          fill="#0A0F17"
          data-testid="acc-headline-value"
        >
          {pct(a)}
        </text>
      </svg>
      <div>
        <div className="data-label mb-2">{t("acc.headline.label")}</div>
        <div className="text-base text-slate-600">
          {t("acc.headline.basis")}{" "}
          <span className="font-mono tabular-nums text-slate-900">{validated}</span>{" "}
          {t("acc.headline.basis2")}
        </div>
        <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-1 text-sm font-mono">
          <div>
            <span className="text-slate-500">Correct: </span>
            <span className="text-emerald-700 tabular-nums">{data.overall.correct}</span>
          </div>
          <div>
            <span className="text-slate-500">Incorrect: </span>
            <span className="text-red-700 tabular-nums">{data.overall.incorrect}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ByRiskClass({ data, t }) {
  const rows = data?.by_risk_class || [];

  if (rows.length === 0) {
    return (
      <div className="border border-slate-200 p-8 clinical-grid">
        <div className="data-label mb-3">{t("acc.byClass.title")}</div>
        <div className="text-sm text-slate-500">{t("acc.byClass.empty")}</div>
      </div>
    );
  }

  return (
    <div className="border border-slate-200" data-testid="acc-by-class">
      <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 data-label">
        {t("acc.byClass.title")}
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left">
            <th className="px-4 py-2 data-label">{t("acc.byClass.class")}</th>
            <th className="px-4 py-2 data-label text-right">{t("acc.byClass.cases")}</th>
            <th className="px-4 py-2 data-label text-right">{t("acc.byClass.correct")}</th>
            <th className="px-4 py-2 data-label text-right">{t("acc.byClass.incorrect")}</th>
            <th className="px-4 py-2 data-label">{t("acc.byClass.accuracy")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.class_en} className="row-hover border-b last:border-b-0 border-slate-100">
              <td className="px-4 py-2">
                <span className="inline-flex items-center gap-2">
                  <span
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: RISK_COLORS[r.class_en] || "#94A3B8" }}
                  />
                  <span className="font-medium text-slate-900">{r.class_en}</span>
                </span>
              </td>
              <td className="px-4 py-2 text-right font-mono tabular-nums text-slate-900">{r.total}</td>
              <td className="px-4 py-2 text-right font-mono tabular-nums text-emerald-700">{r.correct}</td>
              <td className="px-4 py-2 text-right font-mono tabular-nums text-red-700">{r.incorrect}</td>
              <td className="px-4 py-2">
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-1.5 bg-slate-100">
                    <div
                      className="h-full"
                      style={{
                        width: `${Math.max(0, Math.min(1, r.accuracy ?? 0)) * 100}%`,
                        backgroundColor: RISK_COLORS[r.class_en] || "#94A3B8",
                      }}
                    />
                  </div>
                  <span className="font-mono tabular-nums text-xs text-slate-900 min-w-[50px] text-right">
                    {pct(r.accuracy)}
                  </span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ByDayChart({ data, t }) {
  const days = data?.by_day || [];
  const withValidations = days.filter((d) => d.validated > 0);

  if (withValidations.length === 0) {
    return (
      <div className="border border-slate-200 p-8 clinical-grid">
        <div className="data-label mb-3">{t("acc.byDay.title")}</div>
        <div className="text-sm text-slate-500">{t("acc.byDay.empty")}</div>
      </div>
    );
  }

  const w = 720;
  const h = 200;
  const pad = { l: 38, r: 14, t: 16, b: 28 };
  const innerW = w - pad.l - pad.r;
  const innerH = h - pad.t - pad.b;
  const n = days.length;
  const xStep = innerW / Math.max(1, n - 1);

  const points = days.map((d, i) => ({
    x: pad.l + i * xStep,
    y: d.accuracy == null ? null : pad.t + innerH - innerH * d.accuracy,
    date: d.date,
    accuracy: d.accuracy,
    validated: d.validated,
  }));

  const linePath = points
    .filter((p) => p.y != null)
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`)
    .join(" ");

  // y-axis ticks: 0%, 50%, 100%
  const yTicks = [0, 0.5, 1];

  return (
    <div className="border border-slate-200" data-testid="acc-by-day">
      <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
        <div className="data-label">{t("acc.byDay.title")}</div>
        <div className="text-[11px] text-slate-500 mt-0.5 font-mono">{t("acc.byDay.subtitle")}</div>
      </div>
      <div className="p-4 overflow-x-auto">
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="block">
          {yTicks.map((tk) => {
            const y = pad.t + innerH - innerH * tk;
            return (
              <g key={tk}>
                <line x1={pad.l} y1={y} x2={w - pad.r} y2={y} stroke="#E2E8F0" strokeDasharray="3 3" />
                <text
                  x={pad.l - 6}
                  y={y + 3}
                  fontSize="10"
                  fontFamily="IBM Plex Mono, monospace"
                  fill="#64748B"
                  textAnchor="end"
                >
                  {Math.round(tk * 100)}%
                </text>
              </g>
            );
          })}

          {linePath && (
            <path
              d={linePath}
              fill="none"
              stroke="#0055FF"
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          )}

          {points.map(
            (p) =>
              p.y != null && (
                <g key={p.date}>
                  <circle cx={p.x} cy={p.y} r="3.5" fill="#0055FF" />
                  <title>
                    {p.date} · {pct(p.accuracy)} · {p.validated} cases
                  </title>
                </g>
              )
          )}

          {/* x-axis: first/middle/last labels only */}
          {[0, Math.floor(n / 2), n - 1].map((i) => {
            const p = points[i];
            if (!p) return null;
            return (
              <text
                key={i}
                x={p.x}
                y={h - 8}
                fontSize="10"
                fontFamily="IBM Plex Mono, monospace"
                fill="#64748B"
                textAnchor="middle"
              >
                {p.date.slice(5)}
              </text>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

function ByCaller({ data, t }) {
  const rows = data?.by_caller || [];

  if (rows.length === 0) {
    return (
      <div className="border border-slate-200 p-8 clinical-grid">
        <div className="data-label mb-3">{t("acc.byCaller.title")}</div>
        <div className="text-sm text-slate-500">{t("acc.byCaller.empty")}</div>
      </div>
    );
  }

  return (
    <div className="border border-slate-200" data-testid="acc-by-caller">
      <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 data-label">
        {t("acc.byCaller.title")}
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left">
            <th className="px-4 py-2 data-label">{t("acc.byCaller.email")}</th>
            <th className="px-4 py-2 data-label text-right">{t("acc.byCaller.cases")}</th>
            <th className="px-4 py-2 data-label text-right">{t("acc.byCaller.accuracy")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.email} className="row-hover border-b last:border-b-0 border-slate-100">
              <td className="px-4 py-2 font-mono text-[12px] text-slate-900">{r.email}</td>
              <td className="px-4 py-2 text-right font-mono tabular-nums text-slate-900">
                {r.validated}
              </td>
              <td className="px-4 py-2 text-right font-mono tabular-nums text-slate-900">
                {pct(r.accuracy)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AccuracyPage() {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const params = days === "all" ? { all_time: true } : { days };
        const r = await axios.get(`${API}/transactions/accuracy`, { params });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || String(e.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [days, tick]);

  const refresh = () => setTick((n) => n + 1);

  const summary = useMemo(() => {
    const o = data?.overall || {};
    const coverage = o.total > 0 ? o.validated / o.total : null;
    return [
      { key: "total", label: t("acc.summary.total"), value: o.total ?? 0 },
      { key: "validated", label: t("acc.summary.validated"), value: o.validated ?? 0 },
      { key: "pending", label: t("acc.summary.pending"), value: o.pending ?? 0 },
      {
        key: "coverage",
        label: t("acc.summary.coverage"),
        value: coverage == null ? "—" : pct(coverage),
      },
    ];
  }, [data, t]);

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10" data-testid="accuracy-page">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
        <div>
          <div className="data-label mb-2">{t("acc.kicker")}</div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            {t("acc.title")}
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">{t("acc.description")}</p>
        </div>
        <div className="flex items-center gap-2">
          {WINDOWS.map((w) => (
            <button
              key={w}
              onClick={() => setDays(w)}
              data-testid={`window-${w}`}
              className={[
                "px-3 py-1.5 text-xs font-mono border transition-colors",
                days === w
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50",
              ].join(" ")}
            >
              {t(`acc.window.${w}`)}
            </button>
          ))}
          <button
            onClick={refresh}
            data-testid="btn-refresh-acc"
            className="ml-2 px-3 py-1.5 text-xs font-mono border border-slate-300 hover:bg-slate-50"
          >
            {t("acc.refresh")}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 border border-red-300 bg-red-50 text-red-800 text-sm" data-testid="acc-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="data-label" data-testid="acc-loading">{t("acc.loading")}</div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6 mb-6">
            <HeroAccuracy data={data} t={t} />
            <div className="border border-slate-200" data-testid="acc-summary">
              <div className="grid grid-cols-2 gap-px bg-slate-200">
                {summary.map((s) => (
                  <div key={s.key} className="bg-white p-5" data-testid={`acc-stat-${s.key}`}>
                    <div className="data-label">{s.label}</div>
                    <div className="font-mono text-3xl tabular-nums text-slate-900 mt-1">
                      {s.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mb-6">
            <ByRiskClass data={data} t={t} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6">
            <ByDayChart data={data} t={t} />
            <ByCaller data={data} t={t} />
          </div>
        </>
      )}
    </div>
  );
}
